import { useEffect, useMemo, useState, useRef, useCallback } from "react"

import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"
import { useI18next } from "gatsby-plugin-react-i18next"
import gsap from "gsap"
import { Trans } from "react-i18next"
import styled, { css } from "styled-components"

import LinkButton from "components/buttons/LinkButton"
import Callout from "components/TextElements/Callout"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import { fmobile } from "utils/fullyResponsive"
import useMedia from "utils/useMedia"
import { returnContactUsLink } from "utils/routes"

interface Sections {
  body: string
  link: {
    text: string
    url: string
  }
  img: IGatsbyImageData | undefined
  title?: string
}

interface ButtonsArr {
  buttonText: string
}

interface Gradient {
  gradient: string
  fill: string
}

interface Props {
  gradient?: Gradient
  sections: Sections[]
  buttonsArr: ButtonsArr[]
  calloutText?: { title: string; text: string; buttonText: string }
}

export default function CarouselMobile({
  sections,
  buttonsArr,
  gradient = { gradient: "", fill: "" },
  calloutText = { title: "", text: "", buttonText: "" },
}: Props) {
  const [overflowWrapper, setOverflowWrapper] = useState<HTMLElement | null>(
    null
  )
  const [nextIndex, setNextIndex] = useState(0)
  const timer = useRef(5)
  const [autoPlay, setAutoPlay] = useState(true)
  const { t, language } = useI18next()

  const handleButtonMove = useCallback(
    (index: number) => {
      let buttonWidths = 0
      setNextIndex(index)

      if (overflowWrapper) {
        if (index <= 0) {
          overflowWrapper.scrollLeft = 0
        } else {
          for (let i = 0; i < index; i += 1) {
            const button = document.getElementById(`button_${i}`)
            if (button) {
              const rect = button.getBoundingClientRect()
              buttonWidths += rect.width
            }
          }
          overflowWrapper.scrollTo({ left: buttonWidths, behavior: "smooth" })
        }
      }
    },
    [overflowWrapper]
  )

  useEffect(() => {
    if (overflowWrapper) {
      const tl = gsap.timeline({
        repeat: autoPlay ? -1 : 0,
        repeatDelay: 2,
        repeatRefresh: true,
        onRepeat: () => {
          let index = nextIndex + 1

          if (index >= sections.length) {
            index = 0
          }
          setNextIndex(index)
          handleButtonMove(index)
        },
        scrollTrigger: {
          trigger: `#button_0`,
          start: "bottom 150%",
          // end: "top top",
          toggleActions: "play pause restart restart",
        },
      })

      tl.to({}, timer.current, {})
        .to(`.carousel-div`, {
            duration: 0.3,
            opacity: 0,
          }, 0)

        .set(
          `.carousel-div`,
          {
            display: "none",
          },
          0.3
        )

        .to(`.carousel-button`, {
            duration: 0.3,
            backgroundColor: `${colors.blue10}`,
            color: `${colors.blue800}`,
          }, 0)

        .set(
          `#rotatingDiv_${nextIndex}`,
          {
            display: "flex",
          },
          1
        )

      tl.to(`#rotatingDiv_${nextIndex}`, {
          duration: 0.3,
          opacity: 1,
        }, 1)

      tl.to(`#button_${nextIndex}`, {
          duration: 0.3,
          backgroundColor: `${colors.blue400}`,
          color: "white",
        }, 1)

      return () => {
        tl.kill()
      }
    }
  }, [nextIndex, overflowWrapper, handleButtonMove, sections.length, autoPlay])

  const allButtons = useMemo(() => {
    return buttonsArr.map((data, index) => {
      return (
        <Buttons
          key={`button_${data.buttonText}`}
          className="carousel-button"
          id={`button_${index}`}
          onClick={() => {
            setAutoPlay(false)
            handleButtonMove(index)
          }}
          onKeyUp={() => {
            setAutoPlay(false)
            handleButtonMove(index)
          }}
          role="presentation"
        >
          <Trans>{data.buttonText}</Trans>
        </Buttons>
      )
    })
  }, [buttonsArr, handleButtonMove])

  const rotatingContent = useMemo(() => {
    return sections.map((contentData, index) => {
      return (
        <RotationDiv
          key={`rotatingDiv_${contentData.link.text}`}
          className="carousel-div"
          id={`rotatingDiv_${index}`}
        >
          {contentData.title && (
            <RotationTitle>
              <Trans>{contentData.title}</Trans>
            </RotationTitle>
          )}
          <RotationText>
            <Trans>{contentData.body}</Trans>
          </RotationText>
          <LinkButton gradient={gradient} link={contentData.link.url}>
            <Trans>{contentData.link.text}</Trans>
          </LinkButton>
          <RotatingIllustrationDiv>
            {contentData.img && (
              <RotatingIllustration
                id={`illustration_${index}`}
                alt={`illustration_${index}`}
                image={contentData.img}
                loading="lazy"
              />
            )}
          </RotatingIllustrationDiv>
        </RotationDiv>
      )
    })
  }, [gradient, sections])

  return (
    <OuterWrapper>
      <Wrapper>
        <CalloutDiv>
          <Callout
            variant="lightmode"
            title={{
              content: t(calloutText.title),
              width: "89vw",
              size: useMedia(text.h3, text.h3, text.h3, text.h1Mobile),
            }}
            text={{
              content: t(calloutText.text),
              width: "89vw",
            }}
            buttons={[
              {
                text: t(calloutText.buttonText) ?? "Contact us",
                arrow: true,
                route: returnContactUsLink( language ),
              },
            ]}
          />
        </CalloutDiv>
        <ButtonsBg>
          <OverflowWrapper ref={ref => setOverflowWrapper(ref)}>
            <ButtonRow id="button_row">{allButtons}</ButtonRow>
          </OverflowWrapper>
        </ButtonsBg>
        <CarouselBG>{rotatingContent}</CarouselBG>
      </Wrapper>
    </OuterWrapper>
  )
}

const Buttons = styled.div`
  ${fmobile(css`
    ${text.bodyS};
    height: 30px;
    border-radius: 40px;
    padding: 4px 15px 5px;
    white-space: nowrap;
  `)}
`

const RotationDiv = styled.div`
  ${fmobile(css`
    display: flex;
    flex-direction: column;
    gap: 20px;
    opacity: 0;
    width: 335px;
    height: 588px;
  `)}
`

const RotationTitle = styled.p`
  ${fmobile(css`
    ${text.bodyL};
    color: ${colors.blue800};
  `)}
`

const RotationText = styled.p`
  ${media.mobile} {
    ${text.bodyS};
    color: ${colors.blue800};
    width: 89.333vw;
  }
`

const RotatingIllustrationDiv = styled.div`
  ${media.mobile} {
    position: relative;
    top: 0;
    left: -4.5vw;
    width: 120%;
    height: 86.667vw;
  }
`

const RotatingIllustration = styled(GatsbyImage)`
  ${fmobile(css`
    height: 325px;
    width: 375px;
  `)}
`

const OuterWrapper = styled.div`
  ${fmobile(css`
    background-color: ${colors.lightMode};
    padding: 60px 0px;
    overflow: hidden;
  `)}
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
`

const CalloutDiv = styled.div`
  ${fmobile(css`
    width: 335px;
    height: auto;
    margin-left: 20px;
  `)}
`

const ButtonsBg = styled.div`
  ${fmobile(css`
    position: relative;
    overflow: hidden;
    top: 25px;
    z-index: 0;
    background-color: ${colors.blue10};
    padding: 15px 30px;
    width: 100%;
    height: 90px;
    justify-content: center;
    align-items: center;
    border-top-left-radius: 15px;
  `)}
`

const OverflowWrapper = styled.div`
  ${fmobile(css`
    width: 100%;
    overflow: hidden;
    overflow-x: scroll;
    ::-webkit-scrollbar {
      display: none;
    }

    ::before {
      width: 100%;
      height: 100%;
      z-index: 1;
      left: -338px;
      top: 0;
      content: "";
      position: absolute;
    }
  `)}
`

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`

const CarouselBG = styled.div`
  ${fmobile(css`
    position: relative;
    z-index: 1;
    width: 100%;
    height: 500px;
    border-radius: 19px;
    padding: 30px 20px 0px 20px;
    background-color: white;
  `)}
`
